'use client'

import useSWR, { SWRConfiguration } from 'swr'
import { ClientError, RequestDocument } from 'graphql-request'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { useCallback, useMemo } from 'react'
import { useMicrositeSettingsContext } from '@dustin-web/microsite-components/src/hooks/use-microsite-settings'
import { keepPreviousResultMiddleware } from '../middleware'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import {
  GraphqlContext,
  DEFAULT_GRAPHQL_CONTEXT,
  dangerously_getGraphqlFetcher,
  getCacheKeyFromDocument,
} from './graphql-fetcher'

const DEFAULT_SWR_OPTIONS: SWRConfiguration = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
}

export const useGraphql = <TQuery = unknown, TVariables = Record<string, unknown>>(
  document: RequestDocument | TypedDocumentNode<TQuery, TVariables>,
  {
    variables,
    context,
    swrOptions,
  }: {
    variables?: TVariables
    context?: GraphqlContext
    swrOptions?: SWRConfiguration
  } = {},
  shouldQuery: boolean = true
) => {
  const swrOptionsWithDefaults = { ...DEFAULT_SWR_OPTIONS, ...swrOptions }
  const contextWithDefaults = { ...DEFAULT_GRAPHQL_CONTEXT, ...context }
  const micrositeSettings = useMicrositeSettingsContext()
  const { cookieHeader } = useCookieState()
  if (process.env.APP && process.env.NODE_ENV !== 'production') {
    console.error('using useGraphql on app router')
  }
  const fetcher = useMemo(
    () => dangerously_getGraphqlFetcher(micrositeSettings, cookieHeader, contextWithDefaults),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  let cacheKey

  try {
    cacheKey = context?.cacheKey || getCacheKeyFromDocument(document, variables)
  } catch (_error) {
    cacheKey = undefined
  }

  // useSWR skips queries without cache key
  if (context?.skip) {
    cacheKey = null
  }

  const middleware = contextWithDefaults.keepPreviousResult ? [keepPreviousResultMiddleware] : []

  const SWRArgs = {
    cacheKey,
    fetcher: () => fetcher.fetchGraphql<TQuery, TVariables>(document, variables),
    config: {
      use: middleware,
      ...swrOptionsWithDefaults,
    },
  }

  return useSWR<TQuery>(!shouldQuery ? null : SWRArgs.cacheKey, SWRArgs.fetcher, SWRArgs.config)
}

type MutationContext = Omit<GraphqlContext, 'skip' | 'keepPreviousResult' | 'cacheKey'>

export const useGraphqlMutation = <
  TMutation = unknown,
  TMutationVariables = Record<string, unknown>
>(
  document: RequestDocument | TypedDocumentNode<TMutation, TMutationVariables>,
  opts?: {
    context?: MutationContext
    onError?: (error: ClientError) => void
  }
) => {
  if (process.env.APP) {
    console.error('using useGraphqlMutation on app router')
  }
  const contextWithDefaults = { ...DEFAULT_GRAPHQL_CONTEXT, ...opts?.context }
  const micrositeSettings = useMicrositeSettingsContext()
  const { cookieHeader } = useCookieState()

  const fetcher = useMemo(
    () => dangerously_getGraphqlFetcher(micrositeSettings, cookieHeader, contextWithDefaults),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const mutator = useCallback(
    (props?: { variables?: TMutationVariables }) => {
      return fetcher.fetchGraphql<TMutation, TMutationVariables>(
        document,
        props?.variables,
        opts?.onError
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document]
  )

  return [mutator]
}
